'use client';

import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { components } from '@/types/strapi';

interface ContactFormProps {
	script?: components['schemas']['SharedScriptComponent'];
}

const DEFAULT_SCRIPT = {
	src: 'https://mtalkz1.myfreshworks.com/crm/sales/web_forms/fc49e5fa6c2de028b64c55b7d04c8d27a8661940215d3b3da1de96fe2ac2edd1/form.js',
	script_id:
		'fs_fc49e5fa6c2de028b64c55b7d04c8d27a8661940215d3b3da1de96fe2ac2edd1',
	defer: true,
	async: false,
} as const;

let hasPreconnected = false;

function ensurePreconnect() {
	if (hasPreconnected || typeof document === 'undefined') {
		return;
	}

	if (
		document.querySelector(
			'link[rel="preconnect"][href="https://mtalkz1.myfreshworks.com"]',
		)
	) {
		hasPreconnected = true;
		return;
	}

	const preconnect = document.createElement('link');
	preconnect.rel = 'preconnect';
	preconnect.href = 'https://mtalkz1.myfreshworks.com';
	preconnect.crossOrigin = 'anonymous';
	document.head.appendChild(preconnect);
	hasPreconnected = true;
}

const ContactFormFallback = () => (
	<div className="space-y-4 rounded-lg bg-white p-8 shadow-xl">
		<p className="text-2xl font-bold text-gray-900">
			Get Started Risk-free
		</p>
		<p className="text-gray-600">
			14-day Money back Guarantee. No questions asked.
		</p>
		<p className="text-gray-600">
			Contact us at{' '}
			<a href="tel:+919868629924" className="font-medium text-blue-600">
				+91-98686 29924
			</a>{' '}
			or{' '}
			<a
				href="mailto:enquiry@mtalkz.com"
				className="font-medium text-blue-600">
				enquiry@mtalkz.com
			</a>
			.
		</p>
		<Link
			href="/contact-us"
			className="inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white">
			Get In Touch
		</Link>
	</div>
);

const inputClass =
	'block w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500';

// Plain HTML form (no JavaScript needed) handled by /api/contact
const NoScriptContactForm = () => (
	<form
		action="/api/contact"
		method="post"
		className="space-y-4 rounded-lg bg-white p-6 shadow-xl sm:p-8">
		<p className="text-2xl font-bold text-gray-900">
			Get Started Risk-free
		</p>
		<p className="text-gray-600">
			14-day Money back Guarantee. No questions asked.
		</p>
		{[
			{ id: 'name', label: 'Name', type: 'text', auto: 'name' },
			{ id: 'email', label: 'Email', type: 'email', auto: 'email' },
			{ id: 'phone', label: 'Phone', type: 'tel', auto: 'tel' },
		].map(field => (
			<div key={field.id}>
				<label
					htmlFor={`nojs-${field.id}`}
					className="mb-1 block text-sm font-medium text-gray-700">
					{field.label}
				</label>
				<input
					id={`nojs-${field.id}`}
					name={field.id}
					type={field.type}
					autoComplete={field.auto}
					maxLength={255}
					required
					className={inputClass}
				/>
			</div>
		))}
		<div>
			<label
				htmlFor="nojs-message"
				className="mb-1 block text-sm font-medium text-gray-700">
				Message
			</label>
			<textarea
				id="nojs-message"
				name="message"
				rows={4}
				maxLength={255}
				className={inputClass}
			/>
		</div>
		{/* Honeypot: hidden from people, filled by bots */}
		<div
			aria-hidden="true"
			style={{ position: 'absolute', left: '-9999px' }}>
			<label htmlFor="nojs-website">Website</label>
			<input
				id="nojs-website"
				name="website"
				type="text"
				tabIndex={-1}
				autoComplete="off"
			/>
		</div>
		<button
			type="submit"
			className="inline-flex h-12 w-full items-center justify-center rounded-md bg-blue-600 px-6 text-base font-semibold text-white hover:bg-blue-700">
			Get In Touch
		</button>
	</form>
);

export function ContactForm({ script = DEFAULT_SCRIPT }: ContactFormProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const hasLoaded = useRef(false);

	useEffect(() => {
		const src = script?.src;
		const scriptId = script?.script_id;
		if (!src || !scriptId || !containerRef.current) {
			return;
		}

		const container = containerRef.current;

		const injectScript = () => {
			if (hasLoaded.current || !containerRef.current) {
				return;
			}

			ensurePreconnect();

			const scriptElement = document.createElement('script');
			scriptElement.src = src;
			scriptElement.id = scriptId;
			scriptElement.crossOrigin = 'anonymous';
			scriptElement.defer = true;

			containerRef.current.appendChild(scriptElement);
			hasLoaded.current = true;
		};

		// Older mobile webviews may lack IntersectionObserver: load right away
		if (typeof IntersectionObserver === 'undefined') {
			injectScript();
			return;
		}

		const observer = new IntersectionObserver(
			entries => {
				if (entries[0]?.isIntersecting) {
					injectScript();
					observer.disconnect();
				}
			},
			{ rootMargin: '100px' },
		);

		observer.observe(container);

		return () => {
			observer.disconnect();
		};
	}, [script?.src, script?.script_id]);

	if (!script?.src || !script.script_id) {
		return <ContactFormFallback />;
	}

	return (
		<div className="contact-form-wrap relative m-0 min-h-[480px] w-full">
			<div
				ref={containerRef}
				className="contact-form m-0 min-h-[480px] w-full border-transparent bg-transparent p-0"
				suppressHydrationWarning
			/>
			<noscript>
				{/* The embedded form needs JS: hide its empty box, show a plain form */}
				<style>
					{
						'.contact-form-wrap,.contact-form{min-height:0 !important}.contact-form{display:none !important}'
					}
				</style>
				<NoScriptContactForm />
			</noscript>
		</div>
	);
}
